import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  ENRICHABLE_FIELDS,
  MODULE_CATALOG,
  TESTER_MAX_PRODUCTS,
  userHasModule,
  type RunSummary,
  type WritePreviewProduct,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { useTenantReady } from '@/app/tenant-context';
import { CategoryIngestPicker } from '@/components/category-ingest-picker';
import { NoTenantState } from '@/components/no-tenant-state';
import {
  RunProductPreviewPanel,
  WritePreviewEmptyMessage,
} from '@/components/run-product-preview-panel';
import { WriteOperationsPanel } from '@/components/write-operations-panel';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  FIELD_LABEL,
  Input,
  QueryState,
  Spinner,
} from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { cn, formatNumber, formatRelative } from '@/lib/utils';

type RunsTab = 'pipeline' | 'publications';

const RUNS_TABS: Array<{ id: RunsTab; label: string; description: string }> = [
  {
    id: 'pipeline',
    label: 'Pipeline',
    description:
      'Ingestão, auditoria e geração são seguras: nada é escrito na VTEX. Só a escrita altera o catálogo.',
  },
  {
    id: 'publications',
    label: 'Publicações',
    description: MODULE_CATALOG.write_operations.description,
  },
];

const RUN_KIND_LABEL: Record<string, string> = {
  ingestion: 'Ingestão',
  audit: 'Auditoria',
  generation: 'Geração',
  write: 'Escrita',
};

const STATUS_TONE: Record<string, 'neutral' | 'brand' | 'success' | 'critical'> = {
  queued: 'neutral',
  running: 'brand',
  succeeded: 'success',
  failed: 'critical',
  cancelled: 'neutral',
  paused: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  queued: 'na fila',
  running: 'em execução',
  succeeded: 'concluída',
  failed: 'falhou',
  cancelled: 'cancelada',
  paused: 'pausada',
};

function isRunInFlight(status: RunSummary['status']): boolean {
  return status === 'queued' || status === 'running';
}

function RunActions({
  run,
  canControl,
  busy,
  onPause,
  onResume,
}: {
  run: RunSummary;
  canControl: boolean;
  busy: boolean;
  onPause: (runId: string) => void;
  onResume: (runId: string) => void;
}) {
  if (!canControl) return null;

  if (run.status === 'queued' || run.status === 'running') {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        title="Pausar"
        aria-label="Pausar execução"
        onClick={() => onPause(run.id)}
      >
        <Pause className="size-3.5" />
        <span className="md:hidden">Pausar</span>
      </Button>
    );
  }

  if (run.status === 'paused') {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        title="Retomar"
        aria-label="Retomar execução"
        onClick={() => onResume(run.id)}
      >
        <Play className="size-3.5" />
        <span className="md:hidden">Retomar</span>
      </Button>
    );
  }

  return null;
}

const LLM_USAGE_LIMIT_MESSAGE =
  'Limite de uso ou créditos da IA esgotados. Verifique o provedor e tente de novo.';
const LLM_RATE_LIMIT_MESSAGE =
  'Limite temporário de requisições da IA atingido. Aguarde um pouco e tente de novo.';

/** Mensagens longas/técnicas viram texto curto na tabela; o `title` guarda o original. */
function formatRunError(error: string): string {
  const normalized = error.trim();
  if (
    normalized.includes('sem progresso') ||
    normalized.includes('falta de progresso') ||
    normalized.includes('worker provavelmente')
  ) {
    return 'Sem progresso recente — execução interrompida.';
  }

  const lower = normalized.toLowerCase();
  if (
    normalized === LLM_USAGE_LIMIT_MESSAGE ||
    lower.includes('insufficient credits') ||
    lower.includes('insufficient_quota') ||
    lower.includes('créditos da ia esgotados') ||
    lower.includes('llm respondeu 402')
  ) {
    return LLM_USAGE_LIMIT_MESSAGE;
  }
  if (
    normalized === LLM_RATE_LIMIT_MESSAGE ||
    lower.includes('rate limit') ||
    lower.includes('rate_limit') ||
    lower.includes('too many requests') ||
    lower.includes('requisições da ia atingido') ||
    lower.includes('llm respondeu 429')
  ) {
    return LLM_RATE_LIMIT_MESSAGE;
  }

  if (normalized.length <= 90) return normalized;
  return `${normalized.slice(0, 87).trimEnd()}…`;
}

export function RunsPage() {
  const { tenantId, isResolving } = useTenantReady();
  const { can, user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTester = user?.isTester === true;
  const canRun = can('reviewer');
  const canSeePublications = user ? userHasModule(user, 'write_operations') : false;
  const tabParam = searchParams.get('tab');
  const tab: RunsTab =
    tabParam === 'publications' && canSeePublications ? 'publications' : 'pipeline';
  const active = RUNS_TABS.find((item) => item.id === tab) ?? RUNS_TABS[0]!;

  const selectTab = useCallback(
    (next: RunsTab) => {
      setSearchParams(next === 'pipeline' ? {} : { tab: next }, { replace: true });
    },
    [setSearchParams],
  );

  const [ingestionCategoryIds, setIngestionCategoryIds] = useState<number[]>([]);
  const [generationFields, setGenerationFields] = useState<string[]>(['description']);
  const [generationLimit, setGenerationLimit] = useState(
    isTester ? TESTER_MAX_PRODUCTS : 50,
  );
  const [writeLimit, setWriteLimit] = useState(25);
  const [confirmWriteOpen, setConfirmWriteOpen] = useState(false);
  const [excludedGenerationIds, setExcludedGenerationIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [excludedWriteIds, setExcludedWriteIds] = useState<Set<number>>(() => new Set());
  const [generationSearch, setGenerationSearch] = useState('');
  const [writeSearch, setWriteSearch] = useState('');
  const [debouncedGenerationSearch, setDebouncedGenerationSearch] = useState('');
  const [debouncedWriteSearch, setDebouncedWriteSearch] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedGenerationSearch(generationSearch.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [generationSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedWriteSearch(writeSearch.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [writeSearch]);

  const categoryTree = useQuery({
    queryKey: ['catalog', tenantId, 'category-tree'],
    queryFn: () => api.catalog.categoryTree(tenantId as string),
    enabled: tenantId !== null && canRun,
    staleTime: 5 * 60_000,
  });

  const runs = useQuery({
    queryKey: ['runs', tenantId],
    queryFn: () => api.runs.list(tenantId as string),
    enabled: tenantId !== null,
    // Execuções são assíncronas; sem polling a tela mente sobre o progresso.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((run) => run.status === 'queued' || run.status === 'running')
        ? 3000
        : false,
  });

  // Preview só muda quando o worker grava pending/approved/rejected_by_validator —
  // invalidar no enqueue é cedo demais.
  const previousRunStatusesRef = useRef<Map<string, RunSummary['status']> | null>(null);
  useEffect(() => {
    const current = runs.data;
    if (!current || tenantId === null) return;

    const previous = previousRunStatusesRef.current;
    if (previous) {
      let generationFinished = false;
      let writeFinished = false;
      for (const run of current) {
        const prevStatus = previous.get(run.id);
        if (!prevStatus || !isRunInFlight(prevStatus) || isRunInFlight(run.status)) continue;
        if (run.kind === 'generation') generationFinished = true;
        if (run.kind === 'write') writeFinished = true;
      }
      if (generationFinished) {
        void queryClient.invalidateQueries({
          queryKey: ['runs', tenantId, 'generation-preview'],
        });
      }
      if (writeFinished) {
        void queryClient.invalidateQueries({
          queryKey: ['runs', tenantId, 'write-preview'],
        });
      }
    }

    previousRunStatusesRef.current = new Map(current.map((run) => [run.id, run.status]));
  }, [runs.data, tenantId, queryClient]);

  const effectiveGenerationLimit = isTester ? TESTER_MAX_PRODUCTS : generationLimit;

  const generationPreview = useQuery({
    queryKey: [
      'runs',
      tenantId,
      'generation-preview',
      generationFields.slice().sort().join(','),
      effectiveGenerationLimit,
      debouncedGenerationSearch,
    ],
    queryFn: () =>
      api.runs.generationPreview(tenantId as string, {
        fields: generationFields,
        onlyWithFindings: true,
        limit: effectiveGenerationLimit,
        ...(debouncedGenerationSearch ? { q: debouncedGenerationSearch } : {}),
      }),
    enabled: tenantId !== null && can('reviewer') && generationFields.length > 0,
    placeholderData: keepPreviousData,
  });

  const writePreview = useQuery({
    queryKey: ['runs', tenantId, 'write-preview', writeLimit, debouncedWriteSearch],
    queryFn: () =>
      api.runs.writePreview(tenantId as string, {
        limit: writeLimit,
        ...(debouncedWriteSearch ? { q: debouncedWriteSearch } : {}),
      }),
    enabled: tenantId !== null && can('admin'),
    placeholderData: keepPreviousData,
  });

  const generationPreviewKey = (generationPreview.data?.products ?? [])
    .map((item) => item.productId)
    .join(',');
  const writePreviewKey = (writePreview.data?.products ?? [])
    .map((item) => item.productId)
    .join(',');

  useEffect(() => {
    setExcludedGenerationIds(new Set());
  }, [generationPreviewKey]);

  useEffect(() => {
    setExcludedWriteIds(new Set());
  }, [writePreviewKey]);

  const selectedGenerationProducts = useMemo(() => {
    const products = generationPreview.data?.products ?? [];
    return products.filter((product) => !excludedGenerationIds.has(product.productId));
  }, [generationPreview.data?.products, excludedGenerationIds]);

  const selectedWriteProducts = useMemo(() => {
    const products = writePreview.data?.products ?? [];
    return products.filter((product) => !excludedWriteIds.has(product.productId));
  }, [writePreview.data?.products, excludedWriteIds]);

  const selectedSuggestionIds = useMemo(
    () => selectedWriteProducts.flatMap((product) => product.suggestionIds),
    [selectedWriteProducts],
  );

  function useStart<T>(start: (tenantId: string, body: T) => Promise<RunSummary>, label: string) {
    return useMutation({
      mutationFn: (body: T) => start(tenantId as string, body),
      onSuccess: () => {
        toast.success(`${label} enfileirada`);
        void queryClient.invalidateQueries({ queryKey: ['runs', tenantId] });
        if (label === 'Escrita') {
          void queryClient.invalidateQueries({ queryKey: ['runs', tenantId, 'write-preview'] });
        }
        if (label === 'Geração') {
          void queryClient.invalidateQueries({
            queryKey: ['runs', tenantId, 'generation-preview'],
          });
        }
      },
      onError: (error: unknown) =>
        toast.error(error instanceof ApiError ? error.message : `Falha ao iniciar ${label}`),
    });
  }

  const ingestion = useStart(api.runs.startIngestion, 'Ingestão');
  const audit = useStart(api.runs.startAudit, 'Auditoria');
  const generation = useStart(api.runs.startGeneration, 'Geração');
  const write = useStart(api.runs.startWrite, 'Escrita');

  const pauseRun = useMutation({
    mutationFn: (runId: string) => api.runs.pause(tenantId as string, runId),
    onSuccess: (updated) => {
      toast.success('Execução pausada');
      queryClient.setQueryData<RunSummary[]>(['runs', tenantId], (current) =>
        (current ?? []).map((run) => (run.id === updated.id ? updated : run)),
      );
      void queryClient.invalidateQueries({ queryKey: ['runs', tenantId] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao pausar'),
  });

  const resumeRun = useMutation({
    mutationFn: (runId: string) => api.runs.resume(tenantId as string, runId),
    onSuccess: (updated) => {
      toast.success('Execução retomada');
      queryClient.setQueryData<RunSummary[]>(['runs', tenantId], (current) =>
        (current ?? []).map((run) => (run.id === updated.id ? updated : run)),
      );
      void queryClient.invalidateQueries({ queryKey: ['runs', tenantId] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao retomar'),
  });

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  const canWrite = can('admin');
  const runItems = runs.data ?? [];
  const controlBusy = pauseRun.isPending || resumeRun.isPending;

  function canControl(run: RunSummary): boolean {
    if (run.kind === 'write') return canWrite;
    return canRun;
  }

  function startWrite(): void {
    write.mutate({
      suggestionIds: selectedSuggestionIds,
      limit: writeLimit,
    });
  }

  function handleWriteClick(): void {
    if (selectedWriteProducts.length === 0) {
      toast.error('Selecione ao menos um produto para escrever');
      return;
    }
    setConfirmWriteOpen(true);
  }

  function toggleGenerationProduct(productId: number): void {
    setExcludedGenerationIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  function toggleWriteProduct(productId: number): void {
    setExcludedWriteIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  return (
    <>
      <PageHeader title={MODULE_CATALOG.runs.label} description={active.description} />
      {canSeePublications ? <RunsTabs value={tab} onChange={selectTab} /> : null}

      {tab === 'publications' ? (
        <div
          role="tabpanel"
          id="runs-panel-publications"
          aria-labelledby="runs-tab-publications"
        >
          <WriteOperationsPanel embedded />
        </div>
      ) : (
      <div
        role="tabpanel"
        id="runs-panel-pipeline"
        aria-labelledby="runs-tab-pipeline"
        className="space-y-4 p-4 sm:p-6"
      >
        <div className="grid gap-3 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="1. Ingerir catálogo"
              description="Lê produtos e SKUs da VTEX com imagem válida e grava o snapshot local. Produtos já ingeridos são pulados."
            />
            <div className="flex flex-col gap-3 px-5 py-4">
              <CategoryIngestPicker
                options={categoryTree.data ?? []}
                selectedIds={ingestionCategoryIds}
                onChange={setIngestionCategoryIds}
                disabled={!canRun || ingestion.isPending}
                isLoading={categoryTree.isLoading}
                isError={categoryTree.isError}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-[var(--color-muted)]">
                  {isTester
                    ? `Modo tester: até ${TESTER_MAX_PRODUCTS} produtos · somente leitura · só SKUs com URL de imagem.`
                    : 'Somente leitura · só SKUs com URL de imagem · já ingeridos são ignorados.'}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={!canRun || ingestion.isPending}
                  onClick={() =>
                    ingestion.mutate({
                      productIds: [],
                      categoryIds: ingestionCategoryIds,
                      force: false,
                      ...(isTester ? { limit: TESTER_MAX_PRODUCTS } : {}),
                    })
                  }
                >
                  <Play className="size-3.5" />
                  Rodar ingestão
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="2. Auditar qualidade"
              description="Aplica as regras determinísticas sobre o snapshot e recalcula o score."
            />
            <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-[var(--color-muted)]">Não chama a VTEX.</p>
              <Button
                variant="primary"
                size="sm"
                className="w-full sm:w-auto"
                disabled={!canRun || audit.isPending}
                onClick={() => audit.mutate({ productIds: [] })}
              >
                <Play className="size-3.5" />
                Rodar auditoria
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="3. Gerar sugestões"
              description="Chama o modelo apenas com atributos estruturados do snapshot e valida o resultado."
            />
            <div className="space-y-3 px-5 py-4">
              <div className="flex flex-wrap gap-1.5">
                {ENRICHABLE_FIELDS.map((field) => {
                  const active = generationFields.includes(field);
                  return (
                    <button
                      key={field}
                      type="button"
                      onClick={() => {
                        setGenerationFields((current) =>
                          active ? current.filter((f) => f !== field) : [...current, field],
                        );
                      }}
                      className={cn(
                        'rounded-full px-2.5 py-1 text-[11px] font-medium',
                        active
                          ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]'
                          : 'bg-[var(--color-surface-elevated)] text-[var(--color-muted)]',
                      )}
                    >
                      {FIELD_LABEL[field] ?? field}
                    </button>
                  );
                })}
              </div>

              {canRun && generationFields.length > 0 ? (
                <QueryState
                  isLoading={generationPreview.isLoading && !generationPreview.data}
                  isError={generationPreview.isError && !generationPreview.data}
                  loadingLabel="Carregando produtos elegíveis…"
                  errorTitle="Não foi possível pré-visualizar a geração"
                  onRetry={() => void generationPreview.refetch()}
                >
                  {generationPreview.data ? (
                    <RunProductPreviewPanel
                      emptyMessage={
                        generationFields.includes('specifications') &&
                        (generationPreview.data.specsFindingsWithoutTargets ?? 0) > 0
                          ? `Nenhum produto elegível: há findings de especificações, mas as lacunas são só em tipos/SKU ainda não geráveis (ex.: Combo sem lista Admin ou spec só no SKU). Reingira o catálogo para hidratar allowedValues.`
                          : 'Nenhum produto elegível para os campos selecionados.'
                      }
                      searchEmptyMessage="Nenhum produto elegível encontrado para esta busca."
                      search={generationSearch}
                      onSearchChange={setGenerationSearch}
                      isListFetching={
                        generationPreview.isFetching && generationPreview.isPlaceholderData
                      }
                      summary={
                        <>
                          <span className="font-medium text-[var(--color-ink)]">
                            {formatNumber(generationPreview.data.eligibleProductCount)} elegíveis
                          </span>
                          {' · '}
                          piores scores primeiro
                          {(generationPreview.data.specsFindingsWithoutTargets ?? 0) > 0 ? (
                            <>
                              {' · '}
                              <span className="text-[var(--color-muted)]">
                                {formatNumber(generationPreview.data.specsFindingsWithoutTargets)}{' '}
                                com finding de specs sem alvo gerável
                              </span>
                            </>
                          ) : null}
                        </>
                      }
                      products={generationPreview.data.products.map((product) => ({
                        productId: product.productId,
                        productName: product.productName,
                        imageUrl: product.imageUrl,
                        fields: product.fields,
                        detail: `#${product.productId} · score ${
                          product.score == null ? '—' : formatNumber(product.score)
                        }`,
                      }))}
                      selectedCount={selectedGenerationProducts.length}
                      excludedProductIds={excludedGenerationIds}
                      onToggle={toggleGenerationProduct}
                    />
                  ) : null}
                </QueryState>
              ) : null}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {isTester ? (
                  <p className="text-xs text-[var(--color-muted)]">
                    Modo tester: até {TESTER_MAX_PRODUCTS} produtos · 1 sugestão por produto
                  </p>
                ) : (
                  <>
                    <label className="text-xs text-[var(--color-muted)]">
                      Produtos nesta execução
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={1000}
                      className="w-full sm:w-24"
                      value={generationLimit}
                      onChange={(event) => setGenerationLimit(Number(event.target.value))}
                    />
                  </>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full sm:ml-auto sm:w-auto"
                  disabled={
                    !canRun ||
                    generation.isPending ||
                    generationFields.length === 0 ||
                    selectedGenerationProducts.length === 0
                  }
                  onClick={() =>
                    generation.mutate({
                      productIds: selectedGenerationProducts.map((product) => product.productId),
                      fields: generationFields as never,
                      onlyWithFindings: true,
                      limit: effectiveGenerationLimit,
                    })
                  }
                >
                  <Play className="size-3.5" />
                  Gerar
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="4. Escrever na VTEX"
              description="Aplica sugestões aprovadas. Relê o produto e aborta se o contentHash mudou."
            />
            <div className="space-y-3 px-5 py-4">
              <p className="text-[11px] leading-snug text-[var(--color-muted)]">
                Ordem: menor productId. A escrita consome as aprovações e altera o catálogo na
                VTEX.
              </p>

              {canWrite ? (
                <QueryState
                  isLoading={writePreview.isLoading && !writePreview.data}
                  isError={writePreview.isError && !writePreview.data}
                  loadingLabel="Carregando produtos prontos…"
                  errorTitle="Não foi possível pré-visualizar a escrita"
                  onRetry={() => void writePreview.refetch()}
                >
                  {writePreview.data ? (
                    <RunProductPreviewPanel
                      emptyMessage={<WritePreviewEmptyMessage />}
                      searchEmptyMessage="Nenhum produto com sugestões aprovadas encontrado para esta busca."
                      search={writeSearch}
                      onSearchChange={setWriteSearch}
                      isListFetching={writePreview.isFetching && writePreview.isPlaceholderData}
                      summary={
                        <>
                          <span className="font-medium text-[var(--color-ink)]">
                            {formatNumber(writePreview.data.eligibleProductCount)} produtos prontos
                          </span>
                          {' · '}
                          {formatNumber(writePreview.data.eligibleSuggestionCount)} sugestões
                          aprovadas
                        </>
                      }
                      products={writePreview.data.products.map((product: WritePreviewProduct) => ({
                        productId: product.productId,
                        productName: product.productName,
                        imageUrl: product.imageUrl,
                        fields: product.fields,
                        detail: `#${product.productId} · ${formatNumber(product.suggestionIds.length)} sugestões · ${formatNumber(product.batchCount)} operações VTEX`,
                      }))}
                      selectedCount={selectedWriteProducts.length}
                      excludedProductIds={excludedWriteIds}
                      onToggle={toggleWriteProduct}
                    />
                  ) : null}
                </QueryState>
              ) : (
                <p className="text-[11px] text-[var(--color-muted)]">Escrita exige perfil admin.</p>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="text-xs text-[var(--color-muted)]">Produtos nesta execução</label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  className="w-full sm:w-24"
                  value={writeLimit}
                  onChange={(event) => setWriteLimit(Number(event.target.value))}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full sm:ml-auto sm:w-auto"
                  disabled={
                    !canWrite || write.isPending || selectedWriteProducts.length === 0
                  }
                  onClick={handleWriteClick}
                >
                  <Play className="size-3.5" />
                  Escrever na VTEX
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader title="Execuções recentes" />
          <QueryState
            isLoading={runs.isLoading}
            isError={runs.isError}
            errorTitle="Não foi possível carregar as execuções"
            onRetry={() => void runs.refetch()}
          >
            {runItems.length === 0 ? (
              <EmptyState title="Nenhuma execução ainda" />
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-surface-soft)] text-left text-xs text-[var(--color-muted)]">
                      <tr>
                        <th className="px-5 py-2 font-medium">Tipo</th>
                        <th className="px-5 py-2 font-medium">Status</th>
                        <th className="px-5 py-2 font-medium">Progresso</th>
                        <th className="px-5 py-2 text-right font-medium">Ok</th>
                        <th className="px-5 py-2 text-right font-medium">Falhas</th>
                        <th
                          className="px-5 py-2 text-right font-medium"
                          title="Na geração: produtos só com sugestões barradas pelo validador (veja Fila → Reprovadas)"
                        >
                          Ignorados
                        </th>
                        <th className="px-5 py-2 text-right font-medium">Início</th>
                        <th className="px-5 py-2 text-right font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-hairline)]">
                      {runItems.map((run) => (
                        <tr key={run.id}>
                          <td className="px-5 py-2.5 text-[var(--color-ink)]">
                            {RUN_KIND_LABEL[run.kind] ?? run.kind}
                            {run.dryRun ? (
                              <Badge tone="neutral" className="ml-1.5">
                                dry-run
                              </Badge>
                            ) : null}
                          </td>
                          <td className="px-5 py-2.5">
                            <div className="flex flex-row items-center gap-1">
                              <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>
                                {STATUS_LABEL[run.status] ?? run.status}
                              </Badge>
                              {run.error ? (
                                <p
                                  className="max-w-[14rem] text-[11px] leading-snug text-[var(--color-error)] sm:max-w-xs"
                                  title={run.error}
                                >
                                  {formatRunError(run.error)}
                                </p>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-5 py-2.5">
                            <ProgressCell
                              processed={run.processed}
                              total={run.total}
                              unit={run.kind === 'write' ? 'produtos' : undefined}
                              batchCount={
                                run.kind === 'write' && typeof run.params.batchCount === 'number'
                                  ? run.params.batchCount
                                  : undefined
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {formatNumber(run.succeeded)}
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {formatNumber(run.failed)}
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {formatNumber(run.skipped)}
                          </td>
                          <td className="px-5 py-2.5 text-right text-xs text-[var(--color-muted)]">
                            {formatRelative(run.startedAt ?? run.createdAt)}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="flex justify-end">
                              <RunActions
                                run={run}
                                canControl={canControl(run)}
                                busy={controlBusy}
                                onPause={(id) => pauseRun.mutate(id)}
                                onResume={(id) => resumeRun.mutate(id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y divide-[var(--color-hairline)] md:hidden">
                  {runItems.map((run) => (
                    <div key={run.id} className="space-y-2 px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-[var(--color-ink)]">
                            {RUN_KIND_LABEL[run.kind] ?? run.kind}
                          </p>
                          <p className="text-[11px] text-[var(--color-muted)]">
                            {formatRelative(run.startedAt ?? run.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>
                            {STATUS_LABEL[run.status] ?? run.status}
                          </Badge>
                          <RunActions
                            run={run}
                            canControl={canControl(run)}
                            busy={controlBusy}
                            onPause={(id) => pauseRun.mutate(id)}
                            onResume={(id) => resumeRun.mutate(id)}
                          />
                        </div>
                      </div>
                      <ProgressCell
                        processed={run.processed}
                        total={run.total}
                        unit={run.kind === 'write' ? 'produtos' : undefined}
                        batchCount={
                          run.kind === 'write' && typeof run.params.batchCount === 'number'
                            ? run.params.batchCount
                            : undefined
                        }
                      />
                      <div className="flex flex-wrap gap-2 text-[11px] text-[var(--color-muted)]">
                        <span>ok {formatNumber(run.succeeded)}</span>
                        <span>falhas {formatNumber(run.failed)}</span>
                        <span>ignorados {formatNumber(run.skipped)}</span>
                        {run.dryRun ? <Badge tone="neutral">dry-run</Badge> : null}
                      </div>
                      {run.error ? (
                        <p className="text-[11px] text-[var(--color-error)]" title={run.error}>
                          {formatRunError(run.error)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            )}
          </QueryState>
        </Card>
      </div>
      )}

      <ConfirmDialog
        open={confirmWriteOpen}
        title="Escrever na VTEX?"
        description={
          selectedWriteProducts.length === 0
            ? 'Nenhum produto selecionado.'
            : `Vai enviar PUT completo para ${selectedWriteProducts.length} produto${selectedWriteProducts.length === 1 ? '' : 's'}: ${selectedWriteProducts
                .slice(0, 5)
                .map(
                  (product) =>
                    `#${product.productId}${product.productName ? ` ${product.productName}` : ''}`,
                )
                .join('; ')}${
                selectedWriteProducts.length > 5
                  ? ` e mais ${selectedWriteProducts.length - 5}`
                  : ''
              }. Relê cada produto e aborta se o contentHash mudou. Essa ação altera o catálogo.`
        }
        confirmLabel="Escrever na VTEX"
        tone="danger"
        isPending={write.isPending}
        onCancel={() => setConfirmWriteOpen(false)}
        onConfirm={() => {
          setConfirmWriteOpen(false);
          startWrite();
        }}
      />
    </>
  );
}

function RunsTabs({
  value,
  onChange,
}: {
  value: RunsTab;
  onChange(tab: RunsTab): void;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = RUNS_TABS.findIndex((item) => item.id === value);
    if (index < 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = RUNS_TABS[(index + delta + RUNS_TABS.length) % RUNS_TABS.length]!;
      onChange(next.id);
      document.getElementById(`runs-tab-${next.id}`)?.focus();
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onChange(RUNS_TABS[0]!.id);
      document.getElementById(`runs-tab-${RUNS_TABS[0]!.id}`)?.focus();
    }
    if (event.key === 'End') {
      event.preventDefault();
      const last = RUNS_TABS[RUNS_TABS.length - 1]!;
      onChange(last.id);
      document.getElementById(`runs-tab-${last.id}`)?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Seções de execuções"
      onKeyDown={onKeyDown}
      className="flex gap-1 overflow-x-auto border-b border-[var(--color-hairline)] px-4 sm:px-6"
    >
      {RUNS_TABS.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`runs-tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`runs-panel-${item.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2.5 text-sm transition',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-primary)]',
              selected
                ? 'border-[var(--color-primary)] font-semibold text-[var(--color-ink)]'
                : 'border-transparent font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)]',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function ProgressCell({
  processed,
  total,
  unit,
  batchCount,
}: {
  processed: number;
  total: number;
  unit?: string;
  batchCount?: number;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <div className="min-w-[7rem]">
      <div className="mb-1 flex justify-between text-[11px] tabular-nums text-[var(--color-muted)]">
        <span>
          {formatNumber(processed)} / {formatNumber(total)}
          {unit ? ` ${unit}` : ''}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-elevated)]">
        <div
          className="h-full rounded-full bg-[var(--color-primary)] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {typeof batchCount === 'number' && batchCount > total ? (
        <p className="mt-0.5 text-[10px] text-[var(--color-muted)]">
          {formatNumber(batchCount)} operações VTEX
        </p>
      ) : null}
    </div>
  );
}
